import React, { useState } from "react";
import axios from "axios";
import { STORE_INFO, SERVICES } from "../mock";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import * as LucideIcons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      setSent(true);
      toast({ title: "Message envoyé !", description: "Nous vous répondrons dans les plus brefs délais." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossible d'envoyer le message. Merci de nous appeler.";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0b2960] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-[#ff5a5f]">Contact</div>
          <h1 className="text-3xl md:text-5xl font-black mt-1">Une question ? Contactez-nous</h1>
          <p className="mt-3 text-neutral-200 max-w-2xl mx-auto">Notre équipe est à votre disposition pour toute demande d'information, devis ou dépannage.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0b2960]">Envoyez-nous un message</h2>
          <p className="text-neutral-600 mt-2 text-sm">Nous vous répondrons dans les 24h ouvrées.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Nom *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Sujet *</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" placeholder="Ex : Demande d'information sur un produit" /></div>
            <div><Label>Message *</Label><Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" /></div>
            <Button type="submit" disabled={submitting || sent} className="bg-[#e11d24] hover:bg-[#b8171d] h-11 px-6 font-bold disabled:opacity-70">
              {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...</>) : sent ? (<><Send className="h-4 w-4 mr-2" /> Envoyé !</>) : (<><Send className="h-4 w-4 mr-2" /> Envoyer le message</>)}
            </Button>
          </form>
        </div>

        <div>
          <h2 className="text-2xl font-extrabold text-[#0b2960]">Nos coordonnées</h2>
          <div className="mt-4 space-y-4">
            {[
              { icon: MapPin, title: "Adresse", content: STORE_INFO.address, href: "#map" },
              { icon: Phone, title: "Téléphone", content: STORE_INFO.phone, href: `tel:${STORE_INFO.phone.replace(/\s/g, "")}` },
              { icon: Mail, title: "Email", content: STORE_INFO.email, href: `mailto:${STORE_INFO.email}` },
            ].map((c, i) => (
              <a key={i} href={c.href} className="flex items-start gap-4 p-4 bg-white border border-neutral-200 rounded-lg hover:border-[#0b2960] hover:shadow-md transition-all">
                <div className="h-11 w-11 rounded-full bg-[#0b2960]/10 text-[#0b2960] flex items-center justify-center shrink-0">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-bold text-neutral-800">{c.title}</div>
                  <div className="text-sm text-neutral-600 mt-0.5">{c.content}</div>
                </div>
              </a>
            ))}

            <div className="p-4 bg-white border border-neutral-200 rounded-lg">
              <div className="flex items-center gap-2 font-bold text-neutral-800">
                <Clock className="h-5 w-5 text-[#0b2960]" /> Horaires d'ouverture
              </div>
              <ul className="mt-3 text-sm space-y-1">
                {STORE_INFO.hours.map((h) => (
                  <li key={h.day} className="flex justify-between border-b border-neutral-100 py-1.5 last:border-0">
                    <span className="text-neutral-700 font-semibold">{h.day}</span>
                    <span className={h.time === "Fermé" ? "text-[#e11d24]" : "text-neutral-600"}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <section className="bg-white border-y border-neutral-200 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Nos services</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Services & Dépannage</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => {
              const Icon = LucideIcons[s.icon] || LucideIcons.Star;
              return (
                <div key={i} className="border border-neutral-200 rounded-xl p-6 bg-neutral-50 hover:bg-white hover:shadow-lg transition-all">
                  <div className="h-12 w-12 rounded-full bg-[#0b2960] text-white flex items-center justify-center mb-3">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-[#0b2960]">{s.title}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="map" className="max-w-7xl mx-auto px-4 py-10">
        <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-lg aspect-[16/7]">
          <iframe
            title="Carte EDS ELECTROMENAGER"
            src="https://www.google.com/maps?q=304+avenue+de+la+Capelette,+13010+Marseille&output=embed"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
};

export default Contact;
